import { Injectable } from '@angular/core';

import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Subject } from 'rxjs';
import { first, takeUntil } from 'rxjs/operators';

import { AttachmentsActions, AttachmentsSelectors } from '@core/attachments-data';
import type { Attachment } from '@core/attachments-types';
import { filterBoolean } from '@core/common-legacy';
import { I18nService } from '@core/i18n-legacy';
import { NotificationsController } from '@core/user-interface-legacy';
import { generateUuid, MaxFileSize, validateMaxFileSize } from '@core/utils-legacy';

export interface FileWithData {
  file: File;
  targetId: string | number;
  type: FileDropAreaType;
}

export enum FileDropAreaType {
  Candidate = 'candidate',
  Offer = 'offer',
  Requisition = 'requisition',
}

@Injectable({ providedIn: 'root' })
export class AttachmentUploadService {
  public static FILES_LIMIT: number = 10;

  public filesInUpload$: BehaviorSubject<FileWithData[]> = new BehaviorSubject([]);
  public filesQueue: FileWithData[] = [];
  public failedList: FileWithData[] = [];
  public lastUploadedFile$ = new Subject<{
    attachment: Attachment;
    targetId: string | number;
  }>();
  private filesInUpload: FileWithData[] = [];

  private ngOnDestroy$ = new Subject<void>();

  constructor(
    private notificationsController: NotificationsController,
    private i18n: I18nService,
    private store: Store,
    private actions$: Actions,
  ) {}

  public addToQueue($event: DragEvent, targetId: string | number, type: FileDropAreaType): void {
    const filteredFiles = this.getFilteredFiles($event);
    const assignedFiles = this.addDataToFiles(filteredFiles, targetId, type);
    this.filesQueue = [...this.filesQueue, ...assignedFiles];
  }

  public startUpload(): void {
    if (this.filesQueue.length > 0) {
      this.filesQueue.forEach(fileData => {
        if (!this.filesInUpload.includes(fileData)) {
          this.uploadFile(fileData);
          this.filesInUpload.push(fileData);
        }
      });
      this.filesQueue = [];
      this.updateStream(this.filesInUpload);
    }
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  private addDataToFiles(
    files: File[],
    targetId: string | number,
    type: FileDropAreaType,
  ): FileWithData[] {
    return files.map(file => ({
      file: file,
      targetId: targetId,
      type: type,
    }));
  }

  private getFilteredFiles($event: DragEvent): File[] {
    const transfer = this.getTransfer($event);
    const filteredFiles = [];
    // NOTE: webkitGetAsEntry supports only Chrome, Firefox, Edge
    if (transfer.items) {
      const length = transfer.items.length;
      for (let i = 0; i < length; i++) {
        const entry = transfer.items[i].webkitGetAsEntry();
        if (entry && filteredFiles) {
          if (entry.isFile && filteredFiles.length < AttachmentUploadService.FILES_LIMIT) {
            filteredFiles.push(transfer.files[i]);
          }
        }
      }
    } else {
      // NOTE: fix for Safari && IE
      Object.keys(transfer.files).forEach(objectKey => {
        const file = transfer.files[<number>(<unknown>objectKey)];
        if (
          file.size !== 68 &&
          file.type !== '' &&
          filteredFiles.length < AttachmentUploadService.FILES_LIMIT
        ) {
          filteredFiles.push(file);
        }
      });
    }

    const validFiles = filteredFiles.filter(file =>
      validateMaxFileSize(file, MaxFileSize.File50MB),
    );
    if (validFiles.length !== filteredFiles.length)
      this.notificationsController.error(
        this.i18n.translate('app.common.errors.multi_files_limit_exceeded'),
      );

    return validFiles;
  }

  private resolveUploadKey(type: FileDropAreaType): string {
    if (type === FileDropAreaType.Candidate) return 'candidateId';
    else if (type === FileDropAreaType.Offer) return 'offerId';
    else if (type === FileDropAreaType.Requisition) return 'requisitionId';
    else return '';
  }

  private uploadFile(fileData: FileWithData): void {
    const uuid = generateUuid();
    const { type, targetId, file } = fileData;
    const uploadKey = this.resolveUploadKey(type);

    this.store.dispatch(
      AttachmentsActions.uploadAttachmentPessimistic({
        payload: {
          file,
          correlationId: uuid,
          ...(uploadKey !== '' ? { data: { key: uploadKey, value: targetId } } : undefined),
        },
      }),
    );

    this.actions$
      .pipe(
        ofType(AttachmentsActions.uploadAttachmentPessimisticSuccess),
        takeUntil(this.ngOnDestroy$.asObservable()),
      )
      .subscribe(() => {
        this.onFileUploadCompleted(fileData);
      });

    this.store
      .select(AttachmentsSelectors.byId(uuid))
      .pipe(filterBoolean, takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(attachment => {
        this.onFileUploadSuccess(attachment, fileData);
      });

    this.actions$
      .pipe(
        ofType(AttachmentsActions.uploadAttachmentPessimisticFailed),
        first(),
        takeUntil(this.ngOnDestroy$.asObservable()),
      )
      .subscribe(error => {
        if (uuid === error.payload.correlationId) {
          this.failedList.push(fileData);
          this.onFileUploadCompleted(fileData);
        }
      });
  }

  private onFileUploadCompleted(fileData: FileWithData): void {
    this.filesInUpload = this.filesInUpload.filter(fileInUpload => fileInUpload !== fileData);
    this.updateStream(this.filesInUpload);
  }

  private onFileUploadSuccess(attachment: Attachment, { targetId }: FileWithData): void {
    this.lastUploadedFile$.next({ attachment, targetId });
  }

  private getTransfer($event: DragEvent): DataTransfer {
    return $event.dataTransfer ? $event.dataTransfer : (<any>$event).originalEvent.dataTransfer;
  }

  private updateStream(filesData: FileWithData[]): void {
    this.filesInUpload$.next(filesData);
  }
}
